import { Ellipsis, Pencil, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import TableIconBadge from "@/routes/custom_tables/TableIconBadge";

import ACL_RELATIONSHIPS from "acl-relationships";
import RuvixxDate from "components/RuvixxDate";
import { TableLink } from "components/BaseTableV3/TableLink";
import { checkIsAuthorized } from "components/helpers";

export const getCustomTablesAdminColumns = ({ onEdit, onDelete }) => {
  const canEdit = checkIsAuthorized([ACL_RELATIONSHIPS.customTable.editSchema]);
  const canDelete = checkIsAuthorized([
    ACL_RELATIONSHIPS.customTable.deleteSchema,
  ]);
  return [
    {
      header: "Name",
      id: "Name",
      accessorKey: "display_name",
      cell: ({ row }) => {
        const slug = row.original?.slug;
        const name = row.original?.display_name;
        const iconBadge = (
          <TableIconBadge iconName={row.original?.icon_name} smallIcon />
        );
        if (!slug) {
          return (
            <span className="tw:inline-flex tw:items-center tw:gap-2">
              {iconBadge}
              <span>{name}</span>
            </span>
          );
        }
        return (
          <span className="tw:inline-flex tw:items-center tw:gap-2">
            {iconBadge}
            <TableLink href={`/settings/custom_tables/${slug}/edit`}>
              {name}
            </TableLink>
          </span>
        );
      },
    },
    {
      header: "Table ID",
      id: "Table ID",
      accessorKey: "slug",
      enableSorting: false,
      size: 200,
      cell: ({ row }) => {
        const slug = row.original?.slug;
        if (!slug) return null;
        return (
          <Badge variant="secondary" code>
            {slug}
          </Badge>
        );
      },
    },
    {
      header: "# Columns",
      id: "# Columns",
      accessorKey: "column_count",
      enableSorting: false,
      size: 100,
      cell: ({ row }) => {
        const value =
          row.original?.column_count ?? row.original?.columns?.length ?? 0;
        return <span>{value}</span>;
      },
    },
    {
      header: "Created",
      id: "Created",
      accessorKey: "created_at",
      size: 140,
      cell: ({ row }) => {
        const date = row.original?.created_at;
        return date ? <RuvixxDate date={date} /> : null;
      },
    },
    {
      header: "Created By",
      id: "Created By",
      accessorKey: "created_by_username",
      size: 160,
      cell: ({ row }) => {
        const username = row.original?.created_by_username;
        return <span>{username || "—"}</span>;
      },
    },
    {
      header: <div></div>,
      id: "Actions",
      accessorKey: "actions",
      enableSorting: false,
      enableResizing: false,
      size: 90,
      meta: { isDisplay: true },
      cell: ({ row }) => {
        if (!canEdit && !canDelete) return null;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label="Table actions"
              className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
            >
              <Ellipsis />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canEdit && (
                <DropdownMenuItem onClick={() => onEdit?.(row.original)}>
                  <Pencil />
                  Edit
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  variant="destructive"
                  onClick={() => onDelete?.(row.original)}
                >
                  <Trash2 />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
};
